import React from "react";
import { ArrowUpRight, Lock, LogOut } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function startGoogleLogin() {
  const redirectUrl = window.location.origin + "/admin";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function AdminLogin({ notAdmin = false }) {
  const { user, logout } = useAuth();

  return (
    <main className="min-h-[80vh] bg-[var(--cream)] text-[var(--ink)] grain">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-24">
        <div className="tag-mono mb-6">[ Admin · Restricted ]</div>
        <h1 className="headline-xl max-w-4xl">
          {notAdmin ? (
            <>This account is <span className="italic-serif text-[var(--molten)]">not authorised.</span></>
          ) : (
            <>The <span className="italic-serif text-[var(--molten)]">control room.</span></>
          )}
        </h1>

        <div className="mt-10 grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-7">
            {!notAdmin && (
              <p className="text-lg md:text-xl text-[var(--ink)]/75 leading-relaxed max-w-2xl">
                Sign in with the allowlisted Google account to manage awards, speaking engagements, insights, certifications, case studies, publications, and the contact inbox.
              </p>
            )}
            {notAdmin && (
              <div className="mt-2">
                <p className="text-lg text-[var(--ink)]/75 max-w-2xl leading-relaxed">
                  You are signed in as <span className="font-mono">{user?.email}</span>, but this email is not on the admin allowlist. If this looks wrong, sign out and try the correct Google account.
                </p>
                <button onClick={logout} className="btn-outline mt-8">
                  <span>Sign out</span> <LogOut size={14} />
                </button>
              </div>
            )}

            {!notAdmin && (
              <div className="mt-10 p-8 border border-[var(--ink)]/15 bg-[var(--cream-soft)]">
                <div className="flex items-center justify-between mb-6">
                  <div className="tag-mono">[ Single sign-on ]</div>
                  <Lock size={16} className="text-[var(--molten)]" />
                </div>
                <div className="font-serif text-2xl md:text-3xl mb-3" style={{ letterSpacing: "-0.02em", fontWeight: 700 }}>
                  Emergent Google OAuth
                </div>
                <p className="text-[var(--ink)]/70 leading-relaxed">
                  Sessions are stored as httpOnly cookies and valid for 7 days. Access is restricted by email allowlist configured in the backend.
                </p>
                <button onClick={startGoogleLogin} className="btn-ink mt-8">
                  <span>Sign in with Google</span> <ArrowUpRight size={14} />
                </button>
              </div>
            )}
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="p-8 section-ink grain">
              <div className="tag-mono mb-4">[ What you can do here ]</div>
              <ul className="space-y-3 text-[var(--cream)]/80">
                {[
                  "Add, edit and remove awards",
                  "Manage speaking engagements",
                  "Publish insights (blog posts)",
                  "Maintain certifications",
                  "Curate case studies & publications",
                  "Read and triage the contact inbox",
                ].map((l) => (
                  <li key={l} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 bg-[var(--molten)] shrink-0" />
                    <span>{l}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
